import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { track } from "../lib/analytics";

const EASE = [0.22, 1, 0.36, 1];

const LINKS = [
  { label: "Explore", to: "/explore" },
  { label: "Everyday Resources", short: "Resources", to: "/resources" },
  { label: "What We're Learning", short: "Learning", to: "/learning" },
  { label: "HEARD Finds", short: "Finds", to: "/finds" },
  { label: "Community", to: "/community" },
  { label: "About HEARD", short: "About", to: "/about" },
];

const idOf = (to) => `nav-${to.replace(/\//g, "") || "home"}`;

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 30);
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(100, (window.scrollY / max) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  const goToApp = () => {
    track("get_app_clicked", "navbar");
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById("beta")?.scrollIntoView({ behavior: "smooth", block: "start" }), 260);
    } else {
      document.getElementById("beta")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const isActive = (to) => location.pathname === to;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 border-b transition-all duration-500 ${
        scrolled ? "bg-cream/90 backdrop-blur-xl border-line/60 py-3" : "bg-cream/80 backdrop-blur-md border-transparent py-5 sm:py-6"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 flex items-center justify-between gap-4">
        <Logo tone="default" active={location.pathname === "/"} compact />

        <div className="hidden lg:flex items-center gap-6 xl:gap-9">
          {LINKS.map((l) => {
            const active = isActive(l.to);
            return (
              <button
                key={l.to}
                onClick={() => go(l.to)}
                data-testid={idOf(l.to)}
                aria-current={active ? "page" : undefined}
                className={`relative text-[0.7rem] xl:text-[0.72rem] font-semibold uppercase tracking-[0.16em] transition-colors duration-300 ${
                  active ? "text-sage-deep" : "text-charcoal/80 hover:text-sage-deep"
                }`}
              >
                {l.short || l.label}
                {active && <span className="absolute -bottom-2 left-0 right-0 h-px bg-current" />}
              </button>
            );
          })}
          <button
            onClick={goToApp}
            data-testid="nav-get-app"
            className="rounded-full border border-sage-deep px-6 py-2.5 text-[0.7rem] font-medium uppercase tracking-[0.18em] whitespace-nowrap text-sage-deep transition-all duration-400 hover:bg-sage-deep hover:text-cream"
          >
            Get the App
          </button>
        </div>

        <button
          className="lg:hidden flex items-center justify-center h-10 w-10 rounded-full bg-cream/75 backdrop-blur-sm border border-line/60 text-charcoal shadow-sm"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.32, ease: EASE }}
            className="lg:hidden mt-4 mx-4 rounded-3xl bg-cream border border-line px-6 pt-3 pb-5 flex flex-col shadow-[0_20px_50px_rgba(36,35,32,0.25)]"
            data-testid="nav-mobile-menu"
          >
            <button
              onClick={() => go("/")}
              data-testid="nav-mobile-home"
              className={`flex items-center justify-between py-4 border-b border-line/60 uppercase tracking-[0.16em] text-sm transition-colors ${
                location.pathname === "/" ? "text-sage-deep font-semibold" : "text-charcoal hover:text-sage-deep"
              }`}
            >
              Home
            </button>
            {LINKS.map((l) => {
              const active = isActive(l.to);
              return (
                <button
                  key={l.to}
                  onClick={() => go(l.to)}
                  aria-current={active ? "page" : undefined}
                  data-testid={`nav-mobile-${l.to.replace(/\//g, "")}`}
                  className={`flex items-center justify-between py-4 border-b border-line/60 uppercase tracking-[0.16em] text-sm transition-colors ${
                    active ? "text-sage-deep font-semibold" : "text-charcoal hover:text-sage-deep"
                  }`}
                >
                  {l.label}
                  {active && <span className="h-1.5 w-1.5 rounded-full bg-sage" />}
                </button>
              );
            })}
            <button
              onClick={goToApp}
              data-testid="nav-mobile-get-app"
              className="mt-5 flex items-center justify-center gap-2 rounded-full bg-sage-deep px-6 py-3.5 text-sm uppercase tracking-[0.16em] text-cream transition-colors hover:bg-sage"
            >
              Get the App <ArrowRight size={15} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className="absolute bottom-0 left-0 h-[2px] bg-sage-deep/80 transition-[width] duration-150 ease-out"
        style={{ width: `${progress}%`, opacity: progress > 0.5 ? 1 : 0 }}
        data-testid="nav-scroll-progress"
        aria-hidden="true"
      />
    </header>
  );
};
;
